import { constants } from '../../constants/Constants';
import withLazyLoad from 'hooks/withLazyLoad';
import { AIQ_TABS, AIQ_REGISTRY } from './productConfigs/aiqTabConfig';

const LazyOverView = withLazyLoad(
  () => import('../../containers/OverView'),
  true
);
const LazyOrderStatus = withLazyLoad(
  () => import('../../containers/OrderStatus/'),
  true
);
const LazyValueCreationMetrics = withLazyLoad(
  () => import('../ValueCreationMetrics'),
  true
);
const LazyBeesVsPepsiConnectMetrics = withLazyLoad(
  () => import('../../containers/BeesVsPepsiConnectMetricsContainer'),
  true
);
const LazyApplicationPerformance = withLazyLoad(
  () => import('../../containers/ApplicationPerformance'),
  true
);
const LazyUserBehavior = withLazyLoad(
  () => import('../../containers/UserBehaviorEngagement/'),
  true
);
const LazyApplicationPerformanceRedesigned = withLazyLoad(
  () => import('../../containers/ApplicationPerformanceRedesigned'),
  true
);
const LazySuccessReport = withLazyLoad(
  () => import('../../containers/SuccessReport'),
  true
);

// Define unique tab identifiers.
export const TAB_OVERVIEW = 'overview';
export const TAB_ORDER_BOARD = 'orderBoard';
export const TAB_VALUE_CREATION = 'valueCreation';
export const TAB_BEES_PEPSI = 'beesVsPepsi';
export const TAB_APP_PERFORMANCE_REDESIGNED = 'appPerformanceRedesigned';
export const TAB_USER_ADOPTION = 'userAdoption';
export const TAB_APP_PERFORMANCE = 'appPerformance';
export const TAB_SUCCESS_REPORT = 'successReport';

// Map identifiers to tab configurations.
export const TABS = {
  [TAB_OVERVIEW]: {
    id: TAB_OVERVIEW,
    label: 'Overview',
    component: LazyOverView,
  },
  [TAB_ORDER_BOARD]: {
    id: TAB_ORDER_BOARD,
    label: 'Order Board',
    component: LazyOrderStatus,
  },
  [TAB_VALUE_CREATION]: {
    id: TAB_VALUE_CREATION,
    label: 'Value Creation',
    component: LazyValueCreationMetrics,
  },
  [TAB_BEES_PEPSI]: {
    id: TAB_BEES_PEPSI,
    label: 'Bees Vs PepsiConnect Metrics',
    component: LazyBeesVsPepsiConnectMetrics,
  },
  [TAB_APP_PERFORMANCE_REDESIGNED]: {
    id: TAB_APP_PERFORMANCE_REDESIGNED,
    label: 'App Performance',
    component: LazyApplicationPerformanceRedesigned,
  },
  [TAB_USER_ADOPTION]: {
    id: TAB_USER_ADOPTION,
    label: 'User Adoption',
    component: LazyUserBehavior,
  },
  [TAB_APP_PERFORMANCE]: {
    id: TAB_APP_PERFORMANCE,
    label: 'App Performance',
    component: LazyApplicationPerformance,
  },
  [TAB_SUCCESS_REPORT]: {
    id: TAB_SUCCESS_REPORT,
    label: 'Success Report',
    component: LazySuccessReport,
  },
  // AIQ tabs are imported from aiqTabConfig
  ...AIQ_TABS,
};

// Registry mapping each product to its available tabs.
// Note:
// • Tabs that are always available are added without an isAvailable function.
// • Tabs that should be conditionally available include an isAvailable function that accepts a config object.
// • Tabs that are never available (always false) are omitted.
// • Success Report tabs are added dynamically based on SUCCESS_REPORT_CONFIG
const baseRegistry = {
  [constants.products.PricingAI]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.PepsiConnect]: [
    { tab: TABS[TAB_SUCCESS_REPORT] },
    {
      tab: TABS[TAB_OVERVIEW],
    },
    {
      tab: TABS[TAB_VALUE_CREATION],
      isAvailable: ({ isDevOrQaOrPreProdEnv, betaFeatureToggle, isMFE }) => {
        if (isMFE) {
          return false;
        } else {
          return isDevOrQaOrPreProdEnv() || betaFeatureToggle;
        }
      },
    },
    {
      tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED],
      isAvailable: ({ isDevOrQaOrPreProdEnv, betaFeatureToggle, isMFE }) => {
        if (isMFE) {
          return false;
        } else {
          return isDevOrQaOrPreProdEnv() || betaFeatureToggle;
        }
      },
    },
    {
      tab: TABS[TAB_USER_ADOPTION],
      isAvailable: ({ isDevOrQaOrPreProdEnv, betaFeatureToggle, isMFE }) => {
        if (isMFE) {
          return false;
        } else {
          return isDevOrQaOrPreProdEnv() || betaFeatureToggle;
        }
      },
    },
  ],
  [constants.products.Canvas]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.ManufacturingControlTower]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.MesLite]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.PepIRiS]: [
    {
      tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED],
      isAvailable: ({ isDevOrQaOrPreProdEnv }) => isDevOrQaOrPreProdEnv(),
    },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.ShouldCostModelling]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.R360]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    {
      tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED],
      isAvailable: ({ isDevOrQaOrPreProdEnv }) => isDevOrQaOrPreProdEnv(),
    },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.GuidedSelling]: [
    {
      tab: TABS[TAB_SUCCESS_REPORT],
      isAvailable: ({ selectedModel }) =>
        !selectedModel || selectedModel.id === constants.modals.smartTargets,
    },
    {
      tab: TABS[TAB_OVERVIEW],
      isAvailable: ({ selectedModel }) =>
        !selectedModel || selectedModel.id === constants.modals.suggestedOrder,
    },
    {
      tab: TABS[TAB_VALUE_CREATION],
      isAvailable: ({ selectedModel }) =>
        !selectedModel || selectedModel.id !== constants.modals.smartTargets,
    },
    {
      tab: TABS[TAB_APP_PERFORMANCE],
      isAvailable: ({ selectedModel }) =>
        !selectedModel || selectedModel.id === constants.modals.suggestedOrder,
    },
  ],
  [constants.products.E2EWaste]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_APP_PERFORMANCE] },
    {
      tab: TABS[TAB_USER_ADOPTION],
      isAvailable: ({ betaFeatureToggle }) => betaFeatureToggle,
    },
  ],
  [constants.products.MLT]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_APP_PERFORMANCE] },
    {
      tab: TABS[TAB_USER_ADOPTION],
      isAvailable: ({ betaFeatureToggle }) => betaFeatureToggle,
    },
  ],
  [constants.products.ShipmentVisibility]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE] },
  ],
  [constants.products.Automation]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
  ],
  [constants.products.PromoAI]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.DTC]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
  ],
  [constants.products.MenuPro]: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.o9Solutions]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.PepsiTastyRewards]: [
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
  ],
  [constants.products.GPG]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.MFGPro]: [{ tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] }],
  [constants.products.Archlet]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.APP]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.Kazandirio]: [
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
  ],
  [constants.products.TPO]: [
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.ProfitToServe]: [
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
  [constants.products.IPC]: [{ tab: TABS[TAB_VALUE_CREATION] }],
  [constants.products.Compass]: [{ tab: TABS[TAB_SUCCESS_REPORT] }],
  [constants.products.DTVPortal]: [{ tab: TABS[TAB_SUCCESS_REPORT] }],
  [constants.products.ProcurementHub]: [{ tab: TABS[TAB_SUCCESS_REPORT] }],
  [constants.products.FLAg]: [{ tab: TABS[TAB_SUCCESS_REPORT] }],
  [constants.products.POE]: [{ tab: TABS[TAB_SUCCESS_REPORT] }],
  [constants.products.MCVO]: [{ tab: TABS[TAB_SUCCESS_REPORT] }],
  // AIQ registry is imported from aiqTabConfig
  ...AIQ_REGISTRY,
  // Default configuration if no specific product entry exists.
  default: [
    { tab: TABS[TAB_OVERVIEW] },
    { tab: TABS[TAB_ORDER_BOARD] },
    { tab: TABS[TAB_VALUE_CREATION] },
    { tab: TABS[TAB_BEES_PEPSI] },
    { tab: TABS[TAB_APP_PERFORMANCE_REDESIGNED] },
    { tab: TABS[TAB_USER_ADOPTION] },
  ],
};

// Base registry without Success Report tabs
// Success Report tabs will be added dynamically at runtime using context
export const registry = baseRegistry;
